/**
 * Customer Billing View Component
 * 
 * Displays comprehensive billing information for a customer
 * Features period selection, cost breakdown, and call history
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  LinearProgress,
  TablePagination
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  AccessTime,
  AccountBalance,
  ShowChart,
  Refresh
} from '@mui/icons-material';
import { LLMCustomer } from '../API';
import { useCustomerBilling } from '../hooks/useCustomerBilling';
import { 
  formatCost, 
  formatCallCount, 
  formatTrendChange 
} from '../utils/billingCalculations';
import { 
  formatConfidenceLevel, 
  getForecastDisclaimer,
  calculateCallCost,
  getModelTier
} from '../utils/forecastingUtils';

interface CustomerBillingViewProps {
  selectedCustomer: LLMCustomer | null;
  enableDebugLogging?: boolean; // Optional debug flag for production
}

export const CustomerBillingView: React.FC<CustomerBillingViewProps> = ({ 
  selectedCustomer,
  enableDebugLogging = false // Default to false for production
}) => {
  const {
    data: billingData,
    currentPeriod,
    isLoading,
    isError,
    error,
    isFetching,
    selectedPeriodType,
    availablePeriodTypes,
    setPeriodType,
    validationErrors,
    refetch
  } = useCustomerBilling(selectedCustomer?.id, {
    includeComparison: true,
    usePreview: false,
    forecastOptions: {
      excludeWeekends: true,
      minimumDays: 5
    }
  });

  if (!selectedCustomer) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Select a customer to view billing information
        </Typography>
      </Box>
    );
  }

  if (validationErrors.length > 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <Typography variant="h6">Validation Errors</Typography>
          {validationErrors.map((error, index) => (
            <Typography key={index}>{error}</Typography>
          ))}
        </Alert>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <Typography variant="h6">Error Loading Billing Data</Typography>
          <Typography>{error?.message || 'Unknown error occurred'}</Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          <AccountBalance sx={{ mr: 1, verticalAlign: 'middle' }} />
          Billing & Usage
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          {selectedCustomer.name}
        </Typography>
      </Box>

      {/* Period Selector */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={9}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Time Period</InputLabel>
              <Select
                value={selectedPeriodType}
                onChange={(e) => setPeriodType(e.target.value)}
                label="Time Period"
                disabled={isLoading}
              >
                {availablePeriodTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => {
                if (enableDebugLogging) {
                  console.log('🔄 [Manual Refresh] Starting manual refresh...');
                  console.log('🔄 [Manual Refresh] Customer ID:', selectedCustomer?.id);
                  console.log('🔄 [Manual Refresh] Selected Period:', selectedPeriodType);
                  console.log('🔄 [Manual Refresh] Current Period:', currentPeriod);
                  console.log('🔄 [Manual Refresh] Validation Errors:', validationErrors);
                  console.log('🔄 [Manual Refresh] Current Loading State:', { isLoading, isFetching, isError });
                }
                refetch();
              }}
              disabled={isLoading}
              fullWidth
              sx={{ height: 56 }} // Match the height of the Select component
            >
              {isLoading ? 'Loading...' : 'Refresh'}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Loading Indicator */}
      {(isLoading || isFetching) && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            {isLoading ? 'Loading billing data...' : 'Refreshing data...'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Customer: {selectedCustomer?.id} | Period: {selectedPeriodType} | Check console for debug logs
          </Typography>
        </Box>
      )}

      {/* Main Content */}
      {billingData && (
        <>
          {/* Cost Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Total Cost Card */}
            <Grid item xs={12} md={4}>
              <CostSummaryCard
                title="Total Cost"
                actualCost={billingData.current.metrics.actualCost}
                projectedCost={billingData.current.metrics.totalProjectedCost}
                forecast={billingData.current.metrics.forecast}
                trendChange={billingData.trendIndicators.costChangePercent}
                trendDirection={billingData.trendIndicators.costTrend}
                isPartial={billingData.current.period.isPartial}
              />
            </Grid>

            {/* Standard Tier Card */}
            <Grid item xs={12} md={4}>
              <ModelTierCard
                title="Standard Models"
                calls={billingData.current.metrics.standardCalls}
                cost={billingData.current.metrics.standardCost}
                tierType="standard"
              />
            </Grid>

            {/* Premium Tier Card */}
            <Grid item xs={12} md={4}>
              <ModelTierCard
                title="Premium Models"
                calls={billingData.current.metrics.premiumCalls}
                cost={billingData.current.metrics.premiumCost}
                tierType="premium"
              />
            </Grid>
          </Grid>

          {/* Call History Table */}
          <CallHistoryTable 
            calls={billingData.current.callsData.calls}
            period={billingData.current.period}
            truncated={billingData.current.callsData.truncated}
            enableDebugLogging={enableDebugLogging}
          />

          {/* Performance Info */}
          <PerformanceInfo 
            performance={billingData.current.performance}
            callsData={billingData.current.callsData}
            lastUpdated={billingData.current.lastUpdated}
          />
        </>
      )}
    </Box>
  );
};

// Cost Summary Card Component
interface CostSummaryCardProps {
  title: string;
  actualCost: number;
  projectedCost?: number;
  forecast?: any;
  trendChange: number;
  trendDirection: 'up' | 'down' | 'stable';
  isPartial: boolean;
}

const CostSummaryCard: React.FC<CostSummaryCardProps> = ({
  title,
  actualCost,
  projectedCost,
  forecast,
  trendChange,
  trendDirection,
  isPartial
}) => {
  // Show actual cost as primary, with projection as secondary
  const forecastAmount = projectedCost ? projectedCost - actualCost : 0;

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        
        <Typography variant="h4" color="primary" gutterBottom>
          {formatCost(actualCost)}
        </Typography>

        {isPartial && forecastAmount > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Projected Total: {formatCost(projectedCost || 0)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Forecasted Additional: +{formatCost(forecastAmount)}
            </Typography>
            {forecast && (
              <Tooltip title={getForecastDisclaimer(forecast)}>
                <Chip 
                  size="small" 
                  label={`${formatConfidenceLevel(forecast.confidenceLevel)} confidence`}
                  variant="outlined"
                  sx={{ mt: 1 }}
                />
              </Tooltip>
            )}
          </Box>
        )}

        {Math.abs(trendChange) > 0.1 && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            {trendDirection === 'up' && <TrendingUp color="error" />}
            {trendDirection === 'down' && <TrendingDown color="success" />}
            {trendDirection === 'stable' && <TrendingFlat color="disabled" />}
            <Typography 
              variant="body2" 
              color={trendDirection === 'up' ? 'error' : trendDirection === 'down' ? 'success' : 'text.secondary'}
              sx={{ ml: 0.5 }}
            >
              {formatTrendChange(trendChange, 'cost')} vs previous period
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// Model Tier Card Component
interface ModelTierCardProps {
  title: string;
  calls: number;
  cost: number;
  tierType: 'standard' | 'premium';
}

const ModelTierCard: React.FC<ModelTierCardProps> = ({
  title,
  calls,
  cost,
  tierType
}) => {
  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        
        <Typography variant="h5" gutterBottom>
          {formatCallCount(calls)} calls
        </Typography>
        
        <Typography variant="h6" color={tierType === 'premium' ? 'warning.main' : 'text.secondary'}>
          {formatCost(cost)}
        </Typography>

        <Box sx={{ mt: 1 }}>
          <Chip 
            size="small"
            label={tierType}
            color={tierType === 'premium' ? 'warning' : 'default'}
            variant="outlined"
          />
        </Box>
      </CardContent>
    </Card>
  );
};

// Call History Table Component
interface CallHistoryTableProps {
  calls: any[];
  period: any;
  truncated: boolean;
  enableDebugLogging?: boolean;
}

const CallHistoryTable: React.FC<CallHistoryTableProps> = ({
  calls,
  period,
  truncated,
  enableDebugLogging = false
}) => {
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Calculate pagination
  const totalCalls = calls.length;
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const displayCalls = calls.slice(startIndex, endIndex);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing page size
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" gutterBottom>
        <AccessTime sx={{ mr: 1, verticalAlign: 'middle' }} />
        Recent Calls ({period.label})
      </Typography>

      {truncated && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Call data was truncated for performance. Showing recent calls only.
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 190 }}>Date (UTC)</TableCell>
                <TableCell>Service/Model</TableCell>
                <TableCell align="right">Tokens</TableCell>
                <TableCell align="right">Cost</TableCell>
                <TableCell>Tier</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayCalls.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary">
                      No calls found for this period
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayCalls.map((call) => (
                  <CallHistoryRow key={call.id} call={call} enableDebugLogging={enableDebugLogging} />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination Controls */}
        {totalCalls > 0 && (
          <TablePagination
            component="div"
            count={totalCalls}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Calls per page:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} of ${count} calls`
            }
          />
        )}
      </Paper>
    </Box>
  );
};

// Individual Call History Row
const CallHistoryRow: React.FC<{ call: any; enableDebugLogging?: boolean }> = ({ call, enableDebugLogging = false }) => {
  const callDate = new Date(call.createdAt);
  const totalTokens = (call.tokenCountPrompt || 0) + (call.tokenCountResponse || 0);
  
  // Calculate cost and tier using imported functions
  const cost = calculateCallCost(call);
  const tier = getModelTier(call);

  // Format customer user info (avoid duplicates)
  const formatCustomerUserInfo = () => {
    const { customer_user_id, customer_user_name, customer_user_email } = call;
    
    if (!customer_user_id && !customer_user_name && !customer_user_email) {
      return null;
    }

    const parts: string[] = [];
    const seen = new Set<string>();

    // Add name if present and unique
    if (customer_user_name && !seen.has(customer_user_name.toLowerCase())) {
      parts.push(customer_user_name);
      seen.add(customer_user_name.toLowerCase());
    }

    // Add email if present and unique
    if (customer_user_email && !seen.has(customer_user_email.toLowerCase())) {
      parts.push(customer_user_email);
      seen.add(customer_user_email.toLowerCase());
    }

    // Add ID if present and unique
    if (customer_user_id && !seen.has(customer_user_id.toLowerCase())) {
      parts.push(customer_user_id);
      seen.add(customer_user_id.toLowerCase());
    }

    return parts.join(' • ');
  };

  const customerUserInfo = formatCustomerUserInfo();

  // Debug logging for tier calculation (conditional)
  if (enableDebugLogging) {
    console.log(`🏷️ [Tier Debug] Call ${call.id}:`, {
      callId: call.id,
      serviceName: call.service?.name,
      serviceGroupName: call.service?.group?.name,
      modelName: call.model?.name,
      modelDefaultServiceGroup: call.model?.defaultServiceGroup,
      calculatedTier: tier,
      customerUserInfo: {
        id: call.customer_user_id,
        name: call.customer_user_name,
        email: call.customer_user_email,
        formatted: customerUserInfo
      },
      rawCall: {
        hasService: !!call.service,
        hasServiceGroup: !!call.service?.group,
        hasModel: !!call.model
      }
    });
  }

  return (
    <TableRow>
      <TableCell>
        <Typography variant="body2">
          {callDate.toLocaleDateString()} {callDate.toLocaleTimeString()}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">
          {call.service?.name || call.model?.name || call.modelId || call.serviceId || 'Unknown'}
        </Typography>
       
        {customerUserInfo && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ wordBreak: 'break-word' }}>
            {customerUserInfo}
          </Typography>
        )}
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2">
          {totalTokens.toLocaleString()}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2">
          {formatCost(cost)}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip 
          size="small"
          label={tier}
          color={tier === 'premium' ? 'warning' : 'default'}
          variant="outlined"
        />
      </TableCell>
    </TableRow>
  );
};

// Performance Information Component
interface PerformanceInfoProps {
  performance: any;
  callsData: any;
  lastUpdated?: Date;
}

const PerformanceInfo: React.FC<PerformanceInfoProps> = ({
  performance,
  callsData,
  lastUpdated
}) => {
  return (
    <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
      <Typography variant="caption" color="text.secondary" display="block">
        <ShowChart sx={{ mr: 0.5, fontSize: 'small', verticalAlign: 'middle' }} />
        Performance: {formatCallCount(performance.totalCallsProcessed)} calls processed 
        in {(performance.fetchTimeMs / 1000).toFixed(1)}s 
        ({performance.pagesRetrieved} pages)
        {callsData.truncated && ' • Data truncated'}
        {lastUpdated && ` • Updated: ${lastUpdated.toLocaleTimeString()}`}
      </Typography>
    </Box>
  );
};
